import { GymTypeConfig, GymTypes, NotFoundError } from 'fitbeat.models';
import { AreaCategory } from '../../config/zone-mappings';
import { GymConfigs } from '../../config/gymconfig';
import { WarmupWarmdownConfig } from '../../config/WarmupwarmdownConfig';
import {
  ExerciseSelection, InternalWorkoutReview, InternalWorkoutReviewStatus,
  WorkoutSelection, WorkoutSelectionReview, WorkoutSelectionStatus,
  WorkoutSelectionWithDisplayInfo, Zone,
} from '../../entities';
import {
  BigBodyGroup, CardioOrResistance, Exercise, ExerciseFormat,
} from '../../entities/exercise/exercise';
import { WorkoutSettings } from '../../entities/user/workoutsettings';
import WorkoutSelectionAnalysis from '../../entities/workoutselection/workoutselectionanalysis';
import { ExerciseSessionType, UserExerciseSession } from '../../entities/workoutsession/userexercisesession';
import { UserWorkoutSession } from '../../entities/workoutsession/userworkoutsession';
import { ObjectHashGenerator } from '../../helpers';
import {
  CachedExerciseRepository,
  DbGymRepository,
  ICurrentWorkoutSelectionRepository,
  IExerciseRepository,
  IWorkoutSelectionRepository,
} from '../../persistence';
import { BagContactFilter } from './bagcontactfilter';
import { BodyGroupManualSelectionFilter } from './bodygroupmanualselectionfilter';
import { CurrentWorkoutSelectionManager } from './currentWorkoutSelectionManager';
import { DisabledFilter } from './disabledfilter';
import { EquipmentFilter } from './equipmentfilter';
import { FilterOutNonExerciseFilter } from './exerciseFilter';
import { PauseBreakExerciseSelector, VoiceTipsSelector, WarmupAndWarmdownSelector } from './exerciseselectors';
import { HomeBreakFilter } from './homeBreakFilter';
import { LevelFilter } from './levelfilter';
import { NormalExerciseFormatsFilter } from './normalexerciseformatsfilter';
import { PreferredZoneFilter } from './preferredzonefilter';
import { PseudoRandomGenerator } from './pseudorandomgenerator';
import { ResistanceCardioFilter } from './resistancecardiofilter';
import { SimilarityCodeFilter } from './similaritycodefilter';
import { ThreeBodyGroupsFilter } from './threebodygroupsfilter';
import { isHomeBoxingWorkout } from './boxing-zone-helper';

export enum FilterTypes {
  Strict = 'Strict',
  Lenient = 'Lenient',
}

export interface IFilterExercises {
  filterType: FilterTypes;
  exercises: IFilteredExercisesForZones[];
}

export interface IFilteredExercisesForZones {
  zone: number;
  filteredExercises: Exercise[];
}

export interface IExerciseSelectionFilter {
  description: string; // will be used by simulator and logs
  name: string;
  filter(
    inputExercises: Exercise[],
    exerciseSelection: ExerciseSelection,
    userWorkoutSettings: WorkoutSettings,
    selectedExercisesSoFar: Exercise[],
    workoutStartDate: Date,
    gymType: GymTypes,
  ): Exercise[];
}

export interface IExerciseOccurance {
  exerciseName: string;
  firstUserSetNumber: number;
  lastUsedSetNumber: number;
  numberOfOccurances: number;
}

export interface IDefaultWorkoutSelections {
  [AreaOrGymTypes: string]: {
    [CardioOrResistance: string]: string;
  };
}

export interface IWorkoutSelectionManager {
  clean(): Promise<void>;
  populateWorkoutExerciseSessions(userWorkoutSession: UserWorkoutSession, gymType: GymTypes):
  Promise<WorkoutSelectionAnalysis | void>;
  getExercise(id: string): Promise<Exercise | undefined>;
  getAllWorkouts(): Promise<WorkoutSelectionWithDisplayInfo[]>;
  updatedCachedWorkoutSelectionStats(): Promise<void>;
  filterWorkouts(status: WorkoutSelectionStatus, from: number, to: number): Promise<WorkoutSelection[]>;
  filterFullBodyWarmUps(allExercises: Exercise[]): Exercise[];
  filterLowerBodyWarmUps(allExercises: Exercise[]): Exercise[];
  filterUpperBodyWarmUps(allExercises: Exercise[]): Exercise[];
  filterBackAndArmsBodyWarmUps(allExercises: Exercise[]): Exercise[];
  regenerateAndStoreWorkoutSelection(
    workoutSetNumber: number | Date, workoutSettings: WorkoutSettings,
    reviewerName: string,
    gymType: GymTypes): Promise<WorkoutSelection>;
  generateWorkoutCopyFromSettings(
    workoutSetNumber: number,
    fromWorkoutSettings: WorkoutSettings,
    toWorkoutSettings: WorkoutSettings,
    reviewerName: string,
    gymType: GymTypes): Promise<WorkoutSelection>;
  copyWorkout3rdAnd4thExercises(
    workoutSetNumberToCopyFrom: number,
    workoutSetNumber: number,
    workoutSettings: WorkoutSettings,
    reviewerName: string,
    gymType: GymTypes): Promise<WorkoutSelection>;
  manualSelectionSecondaryExercises(
    workoutSetNumber: number,
    workoutSettings: WorkoutSettings,
    gymTypeConfig: GymTypeConfig,
    gymType: GymTypes): Promise<IFilterExercises[]>;
  getWorkoutSelection(uniqueReference: string): Promise<WorkoutSelection | undefined>;
  getWorkoutSelectionOrThrow(uniqueReference: string): Promise<WorkoutSelection>;
  updateWorkoutSelection(workoutSelection: WorkoutSelection, reviewerName: string): Promise<WorkoutSelection>;
  addReview(workoutId: string, review: InternalWorkoutReview, isPowerReview: boolean): Promise<void>;
  getExerciseOccurancesForAllWorkouts(): Promise<Map<string, IExerciseOccurance>>;
  getWarmupsWarmdownsForToday(
    gymType: GymTypes, date: Date, instructor?: string, workoutCount?: number): Promise<Exercise[]>;
  getBreakVidsForToday(gymType: GymTypes, date: Date, instructor?: string): Promise<Exercise[][]>;
  getDefaultWorkoutSelection(
    workoutSettings: WorkoutSettings,
    gymType: GymTypes,
    area: AreaCategory | undefined,
  ): Promise<WorkoutSelection | undefined>;
}

const fullBody = 'Back & Arms-Lower-Upper';

const defaultWorkoutSelections: IDefaultWorkoutSelections = {
  // area-specific fallbacks
  calisthenic: {
    [CardioOrResistance.Cardio]: `1_home_Cardio_Basic_${fullBody}_nobag_Bodyweight`,
    [CardioOrResistance.Resistance]: `1_home_Resistance_Basic_${fullBody}_nobag_Bodyweight`,
    [CardioOrResistance.Balance]: `1_home_Balance_Basic_${fullBody}_nobag_Bodyweight`,
  },
  dumbbell: {
    [CardioOrResistance.Cardio]: `1_home_Cardio_Basic_${fullBody}_Bodyweight-Dumbbells-Pushbells`,
    [CardioOrResistance.Resistance]: `1_home_Resistance_Basic_${fullBody}_Bodyweight-Dumbbells-Pushbells`,
    [CardioOrResistance.Balance]: `1_home_Balance_Basic_${fullBody}_Bodyweight-Dumbbells-Pushbells`,
  },
  benchAndDumbbell: {
    [CardioOrResistance.Cardio]: `1_home_Cardio_Basic_${fullBody}_Bench-Bodyweight-Dumbbells-Pushbells`,
    [CardioOrResistance.Resistance]: `1_home_Resistance_Basic_${fullBody}_Bench-Bodyweight-Dumbbells-Pushbells`,
    // no bench, using dumbbell again
    [CardioOrResistance.Balance]: `1_home_Balance_Basic_${fullBody}_Bodyweight-Dumbbells-Pushbells`,
  },
  boxing: {
    [CardioOrResistance.Cardio]: `1_home_Cardio_Intermediate_${fullBody}_Bodyweight`,
  },
  // gymtype-specific fallbacks
  [GymTypes.Indoor]: {
    [CardioOrResistance.Cardio]: `1_Cardio_Intermediate_${fullBody}`,
    [CardioOrResistance.Resistance]: `1_Resistance_Intermediate_${fullBody}`,
    [CardioOrResistance.Balance]: `1_Balance_Intermediate_${fullBody}_nobag`,
  },
  [GymTypes.Outdoor]: {
    [CardioOrResistance.Cardio]: `1_outdoor_Cardio_Intermediate_${fullBody}_nobag`,
    [CardioOrResistance.Resistance]: `1_outdoor_Resistance_Intermediate_${fullBody}_nobag`,
  },
  [GymTypes.Home]: {
    [CardioOrResistance.Cardio]: `1_home_Cardio_Intermediate_${fullBody}_nobag_Bodyweight`,
    [CardioOrResistance.Resistance]: `1_home_Resistance_Intermediate_${fullBody}_nobag_Bodyweight`,
    [CardioOrResistance.Balance]: `1_home_Balance_Intermediate_${fullBody}_nobag_Bodyweight`,
  },
};

export class WorkoutSelectionManager implements IWorkoutSelectionManager {
  public static smartExerciseSelection = false;

  public static listOfExercisesUsedByWorkouts: Map<string, IExerciseOccurance>;

  public static orderedSelectionFilters(): IExerciseSelectionFilter[] {
    return [
      new DisabledFilter(),
      new PreferredZoneFilter(),
      new LevelFilter(),
      new BagContactFilter(),
      new ResistanceCardioFilter(),
      new ThreeBodyGroupsFilter(),
      new SimilarityCodeFilter(),
    ];
  }

  public static orderedSelectionFiltersForWorkoutSelection(): IExerciseSelectionFilter[] {
    return [
      new FilterOutNonExerciseFilter(),
      new NormalExerciseFormatsFilter(),
      new HomeBreakFilter(),
      new DisabledFilter(),
      new PreferredZoneFilter(),
      new LevelFilter(),
      new BagContactFilter(),
      new ResistanceCardioFilter(),
      new ThreeBodyGroupsFilter(),
      new EquipmentFilter(),
    ];
  }

  public static lenientManualSelectionFilter(): IExerciseSelectionFilter[] {
    return [
      new FilterOutNonExerciseFilter(),
      new NormalExerciseFormatsFilter(),
      new HomeBreakFilter(),
      new DisabledFilter(),
      new PreferredZoneFilter(),
      new LevelFilter(),
      new BagContactFilter(),
      new ResistanceCardioFilter(),
      new BodyGroupManualSelectionFilter(),
      new EquipmentFilter(),
    ];
  }

  public static orderedSelectionFiltersForBoxingWorkoutSelection(): IExerciseSelectionFilter[] {
    return [
      new FilterOutNonExerciseFilter(),
      new NormalExerciseFormatsFilter(),
      new HomeBreakFilter(),
      new DisabledFilter(),
      new LevelFilter(),
    ];
  }

  public static lenientManualBoxingSelectionFilter(): IExerciseSelectionFilter[] {
    return [
      new FilterOutNonExerciseFilter(),
      new NormalExerciseFormatsFilter(),
      new HomeBreakFilter(),
      new DisabledFilter(),
      new LevelFilter(),
    ];
  }

  public static getDefaultWorkoutSelectionReference(
    gymType: GymTypes,
    areaCategory: AreaCategory | undefined,
    workoutSettings: WorkoutSettings,
  ): string | undefined {
    const getFallbacksByArea = () => {
      if (areaCategory === 'circuit') {
        return undefined; // explicitly dont use area-specific fallbacks for circuit (they use gym types)
      }
      if (areaCategory) {
        return defaultWorkoutSelections[areaCategory];
      }
      return undefined;
    };
    const getFallbacksByGymType = () => defaultWorkoutSelections[gymType];

    const fallbacksByTypeOfWorkout = getFallbacksByArea() || getFallbacksByGymType();
    return fallbacksByTypeOfWorkout[workoutSettings.cardioOrResistance];
  }

  public static populateWorkoutExerciseSessions(
    allExercises: Exercise[],
    userWorkoutSession: UserWorkoutSession,
    gymType: GymTypes,
  ): void {
    const selectedExercisesSoFar = new Array<Exercise>();
    const { settings } = userWorkoutSession;
    const { startTime } = userWorkoutSession;
    const exersizeSelections = userWorkoutSession.exerciseSessions
      .map((userExerciseSession) => ExerciseSelection.fromUserExerciseSession(userExerciseSession));

    const workoutSelection = new WorkoutSelection(
      startTime.getDay(),
      settings.cardioOrResistance,
      settings.allowBagContact,
      settings.level,
      settings.threeBodyGroups || [],
      exersizeSelections,
      gymType,
      settings.equipment,
    );
    const allNonWarmupOrWarmdownExercises = allExercises.filter((e) => !e.exerciseFormats
          || (e.exerciseFormats.findIndex((ef) => WarmupWarmdownConfig.isWarmupOrWarmdown(ef)) === -1));

    const shuffledExerciseSessions = this.getShuffledExerciseSessions(startTime, workoutSelection);
    shuffledExerciseSessions.forEach((exerciseSelection: ExerciseSelection) => {
      if (WorkoutSelectionManager.smartExerciseSelection) {
        WorkoutSelectionManager.selectExerciseForZone(
          allNonWarmupOrWarmdownExercises,
          exerciseSelection,
          settings,
          selectedExercisesSoFar,
          startTime,
          gymType,
        );
      } else {
        WorkoutSelectionManager.selectDummyFormatAndExercises(exerciseSelection);
      }
    });

    userWorkoutSession.exerciseSessions = userWorkoutSession.exerciseSessions.map((exerciseSession, i) => {
      const selectionForZone = workoutSelection.exerciseSelections[i];
      exerciseSession.exercise = selectionForZone.exercise;
      exerciseSession.format = selectionForZone.format;
      exerciseSession.alternateExercise = selectionForZone.alternateExercise as any;
      return exerciseSession;
    });
  }

  public static selectExerciseForZone(
    allExercises: Exercise[],
    exerciseSelection: ExerciseSelection,
    userWorkoutSettings: WorkoutSettings,
    selectedExercisesSoFar: Exercise[],
    workoutStartDate: Date,
    gymType: GymTypes,
  ) {
    const filters = WorkoutSelectionManager.orderedSelectionFilters();
    let filteredExercises = Array.from(allExercises); // start with full library

    filters.forEach((filter) => {
      filteredExercises = WorkoutSelectionManager.filterExercisesForZone(
        filter,
        filteredExercises,
        exerciseSelection,
        userWorkoutSettings,
        selectedExercisesSoFar,
        workoutStartDate,
        gymType,
      );
    });
    if (filteredExercises.length === 0) {
      throw new Error('Expected workout selection filters to return one exercise but was zero');
    }
    const { shuffledFilteredExercises, shuffledExerciseFormats } = WorkoutSelectionManager.shuffleExercisesAndFormats(
      workoutStartDate,
      filteredExercises,
      userWorkoutSettings.cardioOrResistance,
      ObjectHashGenerator.hashNumberForObject(userWorkoutSettings),
    );

    WorkoutSelectionManager.setFormatAndExercisesForZone(
      exerciseSelection,
      shuffledFilteredExercises,
      shuffledExerciseFormats,
      selectedExercisesSoFar,
    );
  }

  public static filterExercisesForZone(
    filter: IExerciseSelectionFilter,
    inputExercises: Exercise[],
    exerciseSelection: ExerciseSelection,
    userWorkoutSettings: WorkoutSettings,
    selectedExercisesSoFar: Exercise[],
    workoutStartDate: Date,
    gymType: GymTypes,
  ): Exercise[] {
    const outputExercises = filter.filter(
      inputExercises,
      exerciseSelection,
      userWorkoutSettings,
      selectedExercisesSoFar,
      workoutStartDate,
      gymType,
    );
      // ignore the filter if its output is empty, since we should always return at least one exercise at the end
      // isHomeBoxingWorkout is temporary
    if (outputExercises.length > 0 || isHomeBoxingWorkout(userWorkoutSettings, gymType)) {
      return outputExercises;
    }
    return inputExercises;
  }

  public static selectDummyFormatAndExercises(
    exerciseSelection: ExerciseSelection,
  ) {
    const dummyExercise = new Exercise('dummy', '', [], false, [CardioOrResistance.Cardio], ['Biceps'], [], [], []);
    dummyExercise.uniqueReferenceNumber = '-1';
    exerciseSelection.exercise = dummyExercise;
    exerciseSelection.format = ExerciseFormat.Burn1FlashCombos;
    exerciseSelection.alternateExercise = dummyExercise;
  }

  public static setFormatAndExercisesForZone(
    exerciseSelection: ExerciseSelection,
    shuffledFilteredExercises: Exercise[],
    shuffledExerciseFormats: ExerciseFormat[],
    selectedExercisesSoFar: Exercise[],
  ) {
    for (const exercise of shuffledFilteredExercises) {
      for (const format of shuffledExerciseFormats) {
        if (exercise.exerciseFormats != null && exercise.exerciseFormats.indexOf(format) > -1) {
          WorkoutSelectionManager.setPrimaryExercise(exerciseSelection, exercise, format, selectedExercisesSoFar);

          if (format === ExerciseFormat.Build1FlashCombos
            || format === ExerciseFormat.Burn1FlashCombos
            || format === ExerciseFormat.BalanceFlashCombo) {
            WorkoutSelectionManager.setAlternateExercise(
              shuffledFilteredExercises,
              exerciseSelection,
              selectedExercisesSoFar,
              format,
            );
          }
          return;
        }
      }
    }

    WorkoutSelectionManager.setFallbackExercise(exerciseSelection, shuffledFilteredExercises);
  }

  private static setPrimaryExercise(
    exerciseSelection: ExerciseSelection,
    exercise: Exercise,
    format: ExerciseFormat,
    selectedExercisesSoFar: Exercise[],
  ) {
    exerciseSelection.exercise = exercise;
    exerciseSelection.format = format;
    selectedExercisesSoFar.push(exerciseSelection.exercise);
  }

  private static setAlternateExercise(
    availableExercises: Exercise[],
    exerciseSelection: ExerciseSelection,
    selectedExercisesSoFar: Exercise[],
    format: ExerciseFormat,
  ) {
    let filteredForAlternateExercise = availableExercises;

    const simCodeFiltered = (new SimilarityCodeFilter()).filter(
      availableExercises,
      exerciseSelection,
      new WorkoutSettings(),
      selectedExercisesSoFar,
    );

    if (simCodeFiltered.length > 0) {
      filteredForAlternateExercise = simCodeFiltered;
    }

    const formatFiltered = filteredForAlternateExercise.filter((e) => e.exerciseFormats.indexOf(format) > -1);
    if (formatFiltered.length > 0) {
      filteredForAlternateExercise = formatFiltered;
    }

    exerciseSelection.alternateExercise = filteredForAlternateExercise[0];
    selectedExercisesSoFar.push(exerciseSelection.alternateExercise);
  }

  private static setFallbackExercise(exerciseSelection: ExerciseSelection, exercises: Exercise[]) {
    exerciseSelection.exercise = exercises[0];
    exerciseSelection.alternateExercise = null!;
    exerciseSelection.format = ExerciseFormat.Burn2Endurance;
  }

  // todo test this method in isolation
  private static shuffleExercisesAndFormats(
    workoutStartDate: Date,
    filteredExercises: Exercise[],
    preferCardioOrResistanceFormat: CardioOrResistance,
    settingsHash = 0,
  ) {
    const settingsSeed = Math.abs(settingsHash) % 100;
    const dateSeed = PseudoRandomGenerator.getSeedFromDate(workoutStartDate);
    const randomSeedFromDateAndSettings = dateSeed + settingsSeed;

    const shuffledFilteredExercises = PseudoRandomGenerator.shuffleArrayPseudoRandomly(
      filteredExercises,
      randomSeedFromDateAndSettings,
    );

    const preferredFormats = Object.keys(ExerciseFormat)
      .map((e) => ExerciseFormat[e])
      .filter((a) => Exercise.getCardioOrResistancePreferenceFromFormat(a) === preferCardioOrResistanceFormat);

    const shuffledPreferredFormats = PseudoRandomGenerator.shuffleArrayPseudoRandomly(
      preferredFormats,
      randomSeedFromDateAndSettings,
    );

    // Cardio is unbalanced toward combos, so always add endurance to front
    if (preferCardioOrResistanceFormat === CardioOrResistance.Cardio) {
      shuffledPreferredFormats.unshift(ExerciseFormat.Burn2Endurance);
    }

    const otherFormats = Object.keys(ExerciseFormat)
      .map((e) => ExerciseFormat[e])
      .filter((a) => Exercise.getCardioOrResistancePreferenceFromFormat(a) !== preferCardioOrResistanceFormat);

    const shuffledExerciseFormats = shuffledPreferredFormats.concat(otherFormats);

    return { shuffledFilteredExercises, shuffledExerciseFormats };
  }

  private static getShuffledExerciseSessions(
    startTime: Date,
    workoutSelection: WorkoutSelection,
  ): ExerciseSelection[] {
    if (!(startTime instanceof Date)) {
      throw new Error('startTime was not set, but is required to create a seed');
    }
    const shuffleSeed = PseudoRandomGenerator.getSeedFromDate(startTime);
    const shuffled = PseudoRandomGenerator.shuffleArrayPseudoRandomly(workoutSelection.exerciseSelections, shuffleSeed);
    return shuffled as ExerciseSelection[];
  }

  public exerciseRepository: IExerciseRepository;

  public workoutSelectionRepository: IWorkoutSelectionRepository;

  constructor(
    exerciseRepository: IExerciseRepository,
    workoutSelectionRepository: IWorkoutSelectionRepository,
    private currentWorkoutSelectionRepo: ICurrentWorkoutSelectionRepository,
  ) {
    this.exerciseRepository = exerciseRepository;
    this.workoutSelectionRepository = workoutSelectionRepository;
  }

  public async clean(): Promise<void> {
    const all = await this.workoutSelectionRepository.getAll();
    for (const workoutSelection of all) {
      // TODO: should this be updating in background?
      this.workoutSelectionRepository.update(workoutSelection)
        .catch((e) => { /* no logger, no-op */ });
    }
  }

  public async populateWorkoutExerciseSessions(userWorkoutSession: UserWorkoutSession, gymType: GymTypes):
  Promise<void> {
    const allExercises = await this.exerciseRepository.getAll();

    if (allExercises == null || allExercises.length === 0) {
      throw new Error('Empty exercise library, unable to populate workout exercises.');
    }
    return WorkoutSelectionManager.populateWorkoutExerciseSessions(allExercises, userWorkoutSession, gymType);
  }

  public async getExercise(id: string): Promise<Exercise | undefined> {
    return this.exerciseRepository.get(id);
  }

  public async getAllWorkouts(): Promise<WorkoutSelectionWithDisplayInfo[]> {
    const workoutSelections = await this.workoutSelectionRepository.getAll();
    this.calcAndCacheWorkoutSelectionStats(workoutSelections);
    const allActiveWorkouts = await this.currentWorkoutSelectionRepo.getAll();

    return workoutSelections.map((workoutSelection: WorkoutSelection) => {
      const mappedSelection = WorkoutSelectionWithDisplayInfo.mapFromWorkoutSelection(workoutSelection);
      const referenceKey = CurrentWorkoutSelectionManager.activeSelectionId(
        workoutSelection.gymType || GymTypes.Indoor,
        mappedSelection.settings,
      );
      const activeWorkoutNumber = allActiveWorkouts[referenceKey];
      mappedSelection.active = !!(activeWorkoutNumber
        && activeWorkoutNumber === mappedSelection.workoutSequenceDate);
      return mappedSelection;
    });
  }

  public async updatedCachedWorkoutSelectionStats() {
    const workoutSelections = await this.workoutSelectionRepository.getAll();
    this.calcAndCacheWorkoutSelectionStats(workoutSelections);
  }

  public calcAndCacheWorkoutSelectionStats(workoutSelections: WorkoutSelection[]) {
    const latestListOfExercisesUsedByWorkouts = this.calculateTotalOccurancesOfExercisesInWorkouts(workoutSelections);
    WorkoutSelectionManager.listOfExercisesUsedByWorkouts = latestListOfExercisesUsedByWorkouts;
    (this.exerciseRepository as CachedExerciseRepository)
      .updateCachedExerciseStats(latestListOfExercisesUsedByWorkouts);
  }

  public async getMotiTipsForToday(gymType: GymTypes, date: Date, instructor: string)
    : Promise<Map<string, { [key: string]: Exercise[] }>> {
    return VoiceTipsSelector.getMotiTipsForToday(this.exerciseRepository, gymType, date, instructor);
  }

  public async getWarmupsWarmdownsForToday(gymType: GymTypes, date: Date, instructor?: string, workoutCount?: number)
    : Promise<Exercise[]> {
    return WarmupAndWarmdownSelector.getWarmupsWarmdownsForToday(
      this.exerciseRepository,
      gymType,
      date,
      instructor,
      workoutCount,
    );
  }

  public async getBreakVidsForToday(gymType: GymTypes, date: Date, instructor?: string)
    : Promise<Exercise[][]> {
    return PauseBreakExerciseSelector.getBreaksForToday(this.exerciseRepository, gymType, date, instructor);
  }

  public async getExerciseOccurancesForAllWorkouts(): Promise<Map<string, IExerciseOccurance>> {
    const workoutSelections = await this.workoutSelectionRepository.getAll();

    const latestListOfExercisesUsedByWorkouts = this.calculateTotalOccurancesOfExercisesInWorkouts(workoutSelections);
    WorkoutSelectionManager.listOfExercisesUsedByWorkouts = latestListOfExercisesUsedByWorkouts;

    return latestListOfExercisesUsedByWorkouts;
  }

  public async filterWorkouts(status: WorkoutSelectionStatus, from: number, to: number): Promise<WorkoutSelection[]> {
    return this.workoutSelectionRepository.filter(status, from, to);
  }

  public filterFullBodyWarmUps(allExercises: Exercise[]): Exercise[] {
    const fullBodyWarmupsDowns = allExercises.filter(
      (ex) => Exercise.get3BodyGroups(ex.bodyGroups).length === 3,
    );
    return fullBodyWarmupsDowns;
  }

  public filterLowerBodyWarmUps(allExercises: Exercise[]): Exercise[] {
    const lowerBodyWarmupsDowns = allExercises.filter(
      (ex) => {
        const bodyGroup = Exercise.get3BodyGroups(ex.bodyGroups);
        const singleBodyGroupExercise = Exercise.get3BodyGroups(ex.bodyGroups).length === 1;
        return bodyGroup[0] === BigBodyGroup.Lower && singleBodyGroupExercise;
      },
    );
    return lowerBodyWarmupsDowns;
  }

  public filterUpperBodyWarmUps(allExercises: Exercise[]): Exercise[] {
    const upperBodyWarmupsDowns = allExercises.filter(
      (ex) => {
        const bodyGroup = Exercise.get3BodyGroups(ex.bodyGroups);
        const singleBodyGroupExercise = Exercise.get3BodyGroups(ex.bodyGroups).length === 1;
        return bodyGroup[0] === BigBodyGroup.Upper && singleBodyGroupExercise;
      },
    );
    return upperBodyWarmupsDowns;
  }

  public filterBackAndArmsBodyWarmUps(allExercises: Exercise[]): Exercise[] {
    const backAndArmsBodyWarmupsDowns = allExercises.filter(
      (ex) => {
        const bodyGroup = Exercise.get3BodyGroups(ex.bodyGroups);
        const singleBodyGroupExercise = Exercise.get3BodyGroups(ex.bodyGroups).length === 1;
        return bodyGroup[0] === BigBodyGroup.BackAndArms && singleBodyGroupExercise;
      },
    );
    return backAndArmsBodyWarmupsDowns;
  }

  public async copyWorkout3rdAnd4thExercises(
    workoutSetNumberToCopyFrom: number,
    workoutSetNumber: number,
    workoutSettings: WorkoutSettings,
    reviewerName: string,
    gymType: GymTypes,
  ): Promise<WorkoutSelection> {
    const uniqueReference = WorkoutSelection.refNr(workoutSetNumber, workoutSettings, gymType);
    const workoutToCopyInto = await this.getWorkoutSelection(uniqueReference);
    if (workoutToCopyInto == null) {
      throw Error(`Expected workout selection with ref nr ${uniqueReference} to exist`);
    }

    const fromWorkoutUniqueReference = WorkoutSelection.refNr(workoutSetNumberToCopyFrom, workoutSettings, gymType);
    const workoutToCopyFrom = await this.getWorkoutSelection(fromWorkoutUniqueReference);
    if (workoutToCopyFrom == null) {
      throw Error(`Workout to copy from not found with ref: ${fromWorkoutUniqueReference}`);
    }
    if (workoutToCopyInto.exerciseSelections.length !== workoutToCopyFrom.exerciseSelections.length) {
      throw Error(
        `From workout ${fromWorkoutUniqueReference} and to workout ${uniqueReference} not same length`,
      );
    }

    for (let i = 0; i < workoutToCopyInto.exerciseSelections.length; i++) {
      const exerciseSelectionToCopyInto = workoutToCopyInto.exerciseSelections[i];
      const exerciseSelectionToCopyFrom = workoutToCopyFrom.exerciseSelections[i];

      if (exerciseSelectionToCopyInto.format === ExerciseFormat.HeroZonePunches) {
        continue; // only has 1 exercise and should not be copied
      }
      exerciseSelectionToCopyInto.secondAlternateExercise = exerciseSelectionToCopyFrom.exercise;
      exerciseSelectionToCopyInto.thirdAlternateExercise = exerciseSelectionToCopyFrom.alternateExercise;
    }

    const workoutSelectionReviewEntry = this.createReviewEntry(reviewerName);
    workoutToCopyInto.workoutSelectionReviewHistory.push(workoutSelectionReviewEntry);
    workoutToCopyInto.internalWorkoutReview.reviewStatus = InternalWorkoutReviewStatus.NeedReview;
    return this.workoutSelectionRepository.add(workoutToCopyInto);
  }

  public async generateWorkoutCopyFromSettings(
    workoutSetNumber: number,
    fromWorkoutSettings: WorkoutSettings,
    toWorkoutSettings: WorkoutSettings,
    reviewerName: string,
    gymType: GymTypes,
  ): Promise<WorkoutSelection> {
    const uniqueReference = WorkoutSelection.refNr(workoutSetNumber, toWorkoutSettings, gymType);
    let workoutToCopyTo = await this.getWorkoutSelection(uniqueReference);
    if (workoutToCopyTo == null) {
      workoutToCopyTo = new WorkoutSelection(
        workoutSetNumber,
        toWorkoutSettings.cardioOrResistance,
        toWorkoutSettings.allowBagContact,
        toWorkoutSettings.level,
        toWorkoutSettings.threeBodyGroups,
        [],
        gymType,
        toWorkoutSettings.equipment,
      );
    }

    const fromWorkoutUniqueReference = WorkoutSelection.refNr(workoutSetNumber, fromWorkoutSettings, gymType);
    const workoutToCopyFrom = await this.getWorkoutSelection(fromWorkoutUniqueReference);
    if (workoutToCopyFrom == null) {
      throw Error(`Intermediate workout to copy from not found with ref: ${fromWorkoutUniqueReference}`);
    }

    workoutToCopyTo.exerciseSelections = workoutToCopyFrom.exerciseSelections;
    const workoutSelectionReviewEntry = this.createReviewEntry(reviewerName);
    workoutToCopyTo.workoutSelectionReviewHistory.push(workoutSelectionReviewEntry);
    workoutToCopyTo.internalWorkoutReview.reviewStatus = InternalWorkoutReviewStatus.NeedReview;
    return this.workoutSelectionRepository.add(workoutToCopyTo);
  }

  public async regenerateAndStoreWorkoutSelection(
    workoutSeed: number | Date,
    workoutSettings: WorkoutSettings,
    reviewerName: string,
    gymType: GymTypes,
  ): Promise<WorkoutSelection> {
    const gymTypeConfig = GymConfigs[gymType];
    const amountOfStages = gymTypeConfig.allowedZones.length;

    let startTime: Date;
    let workoutNumber: number;

    if (workoutSeed instanceof Date) {
      startTime = workoutSeed;
      workoutNumber = startTime.getTime() / (24 * 60 * 60 * 1000);
    } else {
      startTime = new Date(workoutSeed * 24 * 60 * 60 * 1000);
      workoutNumber = workoutSeed;
    }

    const userWorkoutSession = new UserWorkoutSession(
      'bogus',
      amountOfStages,
      workoutSettings,
      undefined,
      startTime,
      undefined,
      new Date(),
    );

    userWorkoutSession.startTime = startTime;
    userWorkoutSession.settings = workoutSettings;
    for (const session of userWorkoutSession.exerciseSessions) {
      session.stationNumber = Zone.firstStationForZoneNumber(session.workoutStage - 2);
      session.zone = Zone.zoneForStation(session.stationNumber, DbGymRepository.getTestGym());
    }

    await this.populateWorkoutExerciseSessions(userWorkoutSession, gymType);

    const exerciseSelections = new Array<ExerciseSelection>();
    userWorkoutSession.exerciseSessions.forEach((userExerciseSession: UserExerciseSession, index: number) => {
      const exerciseSelection = ExerciseSelection.fromUserExerciseSession(userExerciseSession);
      exerciseSelection.zone = gymTypeConfig.allowedZones[index];
      exerciseSelections.push(exerciseSelection);
    });

    const workout = new WorkoutSelection(
      workoutNumber,
      workoutSettings.cardioOrResistance,
      workoutSettings.allowBagContact,
      workoutSettings.level,
      workoutSettings.threeBodyGroups,
      exerciseSelections,
      gymType,
      workoutSettings.equipment,
    );

    let updatedWorkoutSelection = await this.workoutSelectionRepository.get(workout.uniqueReference);
    if (updatedWorkoutSelection) {
      updatedWorkoutSelection.exerciseSelections = workout.exerciseSelections;
    } else {
      updatedWorkoutSelection = workout;
    }

    const workoutSelectionReviewEntry = this.createReviewEntry(reviewerName);
    updatedWorkoutSelection.workoutSelectionReviewHistory.push(workoutSelectionReviewEntry);
    updatedWorkoutSelection.internalWorkoutReview.reviewStatus = InternalWorkoutReviewStatus.NeedReview;

    return this.workoutSelectionRepository.update(updatedWorkoutSelection);
  }

  public async manualSelectionSecondaryExercises(
    workoutSetNumber: number,
    workoutSettings: WorkoutSettings,
    gymTypeConfig: GymTypeConfig,
    gymType: GymTypes,
  ): Promise<IFilterExercises[]> {
    const allExercises = await this.exerciseRepository.getAll();
    const startTime = new Date(workoutSetNumber * 24 * 60 * 60 * 1000);

    const filteredExercises: IFilterExercises[] = [];
    const strictFilterOptions = isHomeBoxingWorkout(workoutSettings, gymType)
      ? WorkoutSelectionManager.orderedSelectionFiltersForBoxingWorkoutSelection()
      : WorkoutSelectionManager.orderedSelectionFiltersForWorkoutSelection();
    const filterExercise = this.filterExercise(
      allExercises,
      strictFilterOptions,
      startTime,
      workoutSettings,
      gymTypeConfig,
      gymType,
    );

    filteredExercises.push({
      filterType: FilterTypes.Strict,
      exercises: filterExercise,
    });

    const lenientFilterOptions = isHomeBoxingWorkout(workoutSettings, gymType)
      ? WorkoutSelectionManager.lenientManualBoxingSelectionFilter()
      : WorkoutSelectionManager.lenientManualSelectionFilter();
    const lenientManualSelectionExercises = this.filterExercise(
      allExercises,
      lenientFilterOptions,
      startTime,
      workoutSettings,
      gymTypeConfig,
      gymType,
    );

    filteredExercises.push({
      filterType: FilterTypes.Lenient,
      exercises: lenientManualSelectionExercises,
    });

    return filteredExercises;
  }

  public async getWorkoutSelection(uniqueReference: string): Promise<WorkoutSelection | undefined> {
    const workoutSelection = await this.workoutSelectionRepository.get(uniqueReference);
    // Todo: Load workoutSelection from gym config the populated using Zones for the gym
    if (workoutSelection != null) {
      const validExerciseSelections = WorkoutSelection.exerciseSelectionsForActiveZoneNumbers(workoutSelection);
      if (validExerciseSelections != null) {
        workoutSelection.exerciseSelections = validExerciseSelections;
      }

      // default to true of not set, due to a data issue that gave many on bag workouts empty settings
      if (workoutSelection.settings.allowBagContact == null) {
        workoutSelection.settings.allowBagContact = false;
      }
      return workoutSelection;
    }
    return undefined;
  }

  public async getWorkoutSelectionOrThrow(uniqueReference: string): Promise<WorkoutSelection> {
    const workoutSelection = await this.workoutSelectionRepository.get(uniqueReference);
    if (!workoutSelection) {
      throw new NotFoundError('WorkoutSelection', uniqueReference);
    }
    return workoutSelection;
  }

  public async updateWorkoutSelection(
    workoutSelection: WorkoutSelection,
    reviewerName: string,
  ): Promise<WorkoutSelection> {
    const workoutSelectionReviewEntry = this.createReviewEntry(reviewerName);
    workoutSelection.workoutSelectionReviewHistory.push(workoutSelectionReviewEntry);

    workoutSelection.internalWorkoutReview.reviewStatus = InternalWorkoutReviewStatus.NeedReview;
    return this.workoutSelectionRepository.update(workoutSelection);
  }

  public async addReview(workoutId: string, review: InternalWorkoutReview, isPowerReview: boolean): Promise<void> {
    if (isPowerReview) {
      return this.workoutSelectionRepository.addPowerReview(workoutId, review);
    }

    await this.updateAllExerciseSelectionsToHasBeenReviewed(workoutId);
    return this.workoutSelectionRepository.addReview(workoutId, review);
  }

  public async getDefaultWorkoutSelection(
    workoutSettings: WorkoutSettings,
    gymType: GymTypes,
    area: AreaCategory | undefined,
  ): Promise<WorkoutSelection | undefined> {
    const defaultWorkoutSelectionRef = WorkoutSelectionManager
      .getDefaultWorkoutSelectionReference(gymType, area, workoutSettings);
    if (defaultWorkoutSelectionRef) {
      return this.getWorkoutSelection(defaultWorkoutSelectionRef);
    }
    return undefined;
  }

  public async populateExercises(exerciseSelections: ExerciseSelection[]) {
    for (const exerciseSelection of exerciseSelections) {
      exerciseSelection.exercise = await this.exerciseRepository.get(
        exerciseSelection.exercise.uniqueReferenceNumber,
      ) as Exercise;

      if (exerciseSelection.alternateExercise
        && exerciseSelection.alternateExercise.uniqueReferenceNumber) {
        exerciseSelection.alternateExercise = await this.exerciseRepository.get(
          exerciseSelection.alternateExercise.uniqueReferenceNumber,
        ) as Exercise;
      }
      if (exerciseSelection.secondAlternateExercise
        && exerciseSelection.secondAlternateExercise.uniqueReferenceNumber) {
        exerciseSelection.secondAlternateExercise = await this.exerciseRepository.get(
          exerciseSelection.secondAlternateExercise.uniqueReferenceNumber,
        ) as Exercise;
      }
      if (exerciseSelection.thirdAlternateExercise
        && exerciseSelection.thirdAlternateExercise.uniqueReferenceNumber) {
        exerciseSelection.thirdAlternateExercise = await this.exerciseRepository.get(
          exerciseSelection.thirdAlternateExercise.uniqueReferenceNumber,
        ) as Exercise;
      }
    }
  }

  private createReviewEntry = (reviewerName: string): WorkoutSelectionReview => ({
    reviewerName,
    lastUpdated: new Date(),
  });

  private async updateAllExerciseSelectionsToHasBeenReviewed(workoutId: string) {
    const workoutSelection = await this.getWorkoutSelection(workoutId);
    if (workoutSelection != null) {
      for (const exeriseSelection of workoutSelection.exerciseSelections) {
        exeriseSelection.changeHasNotBeenReviewed = false;
      }
      await this.workoutSelectionRepository.update(workoutSelection);
    }
  }

  private filterExercise(
    allExercises: Exercise[],
    filters: IExerciseSelectionFilter[],
    startTime: Date,
    workoutSettings: WorkoutSettings,
    gymTypeConfig: GymTypeConfig,
    gymType: GymTypes,
  ): IFilteredExercisesForZones[] {
    const selectedExercisesSoFar = new Array<Exercise>();
    const filteredExercisesForZones: IFilteredExercisesForZones[] = [];

    const shrinkedAllExercises = Exercise.shrinkExercisesByRemovingHistoryAndInstructions(allExercises);

    for (const zoneTypeNumber of gymTypeConfig.allowedZones) {
      const stationNumber = (zoneTypeNumber - 1) * 2;
      const dummyExerciseSession = new UserExerciseSession(ExerciseSessionType.User, stationNumber);
      // Need to force dummy session format txo a home format for filters
      if (gymType === GymTypes.Home) {
        dummyExerciseSession.format = ExerciseFormat.HomeQuarterSetup;
      }
      const exerciseSelection = ExerciseSelection.fromZoneTypeAndUserExerciseSession(
        zoneTypeNumber,
        dummyExerciseSession,
      );

      let filteredExercises = Array.from(shrinkedAllExercises);
      filters.map((filter: IExerciseSelectionFilter) => {
        filteredExercises = WorkoutSelectionManager.filterExercisesForZone(
          filter,
          filteredExercises,
          exerciseSelection,
          workoutSettings,
          selectedExercisesSoFar,
          startTime,
          gymType,
        );
      });

      filteredExercises = filteredExercises.sort((a, b) => a.numberOfOccurances - b.numberOfOccurances);

      filteredExercisesForZones.push({
        zone: exerciseSelection.zone,
        filteredExercises,
      });
    }

    return filteredExercisesForZones;
  }

  private calculateTotalOccurancesOfExercisesInWorkouts(
    workoutSelections: WorkoutSelection[],
  ): Map<string, IExerciseOccurance> {
    const getCountValueForExercise = (uniqueReference: string) => {
      let currentCount = 0;

      const workoutExercise = latestListOfExercisesUsedByWorkouts[uniqueReference];
      if (workoutExercise) {
        currentCount = workoutExercise.numberOfOccurances;
      }

      return currentCount;
    };

    const getfirstUsedSetNumber = (uniqueReference: string, setNumber: number) => {
      let currentSetNumber = setNumber;
      const workoutExercise = latestListOfExercisesUsedByWorkouts[uniqueReference];
      if (workoutExercise) {
        const exerciseSetNumber = workoutExercise.firstUserSetNumber;
        if (exerciseSetNumber < currentSetNumber) {
          currentSetNumber = exerciseSetNumber;
        }
      }

      return currentSetNumber;
    };
    const latestListOfExercisesUsedByWorkouts = new Map<string, IExerciseOccurance>();
    workoutSelections.forEach((workoutSelection: WorkoutSelection) => {
      workoutSelection.exerciseSelections.forEach((exerciseSelection: ExerciseSelection) => {
        const { exercise: { name, uniqueReferenceNumber } } = exerciseSelection;
        const setNumber = workoutSelection.workoutSequenceDate;

        const firstUserSetNumber = getfirstUsedSetNumber(uniqueReferenceNumber, setNumber);
        const exerciseOccurance: IExerciseOccurance = {
          exerciseName: name,
          firstUserSetNumber,
          lastUsedSetNumber: setNumber,
          numberOfOccurances: getCountValueForExercise(uniqueReferenceNumber) + 1,
        };
        latestListOfExercisesUsedByWorkouts[uniqueReferenceNumber] = exerciseOccurance;

        if (exerciseSelection.alternateExercise) {
          const {
            alternateExercise: {
              name: alternativeExerciseName,
              uniqueReferenceNumber: alternativeUniqueReferenceNumber,
            },
          } = exerciseSelection;

          const alternativeFirstUserSetNumber = getfirstUsedSetNumber(alternativeUniqueReferenceNumber, setNumber);

          const altExerciseOccurance: IExerciseOccurance = {
            exerciseName: alternativeExerciseName,
            firstUserSetNumber: alternativeFirstUserSetNumber,
            lastUsedSetNumber: setNumber,
            numberOfOccurances: getCountValueForExercise(alternativeUniqueReferenceNumber) + 1,
          };
          latestListOfExercisesUsedByWorkouts[alternativeUniqueReferenceNumber] = altExerciseOccurance;
        }
      });
    });
    return latestListOfExercisesUsedByWorkouts;
  }
}
